import { collection, doc, deleteDoc, setDoc, Timestamp } from 'firebase/firestore';

import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import Handbook, { HANDBOOK_STATUSES } from '../types/Handbook';

export const useHandbookMutators = () => {
  const { isOrgAdmin, userState } = useData();
  const { isGod } = useAuth();

  const addHandbook = async (handbook: Handbook): Promise<Handbook | null> => {
    if (userState.status !== 'ready' || !userState.userProfile) {
      console.error('User profile not found');
      return null;
    }

    if (!handbook.orgId) {
      console.error('Handbook orgId not found');
      return null;
    }

    if (!isOrgAdmin(handbook.orgId)) {
      console.error('Permission denied: must be admin of organization');
      return null;
    }

    try {
      // Use provided itemId if available, otherwise generate a new one
      const handbookRef = handbook.itemId
        ? doc(firestore, `org/${handbook.orgId}/handbook/${handbook.itemId}`)
        : doc(collection(firestore, `org/${handbook.orgId}/handbook`));
      const newHandbook: Handbook = {
        ...handbook,
        creatorId: userState.userProfile?.itemId,
        createTime: Timestamp.now(),
        itemId: handbookRef.id,
        history: [],
      };

      await setDoc(handbookRef, newHandbook);
      return newHandbook;
    } catch (error) {
      console.error('Error adding handbook:', error);
      return null;
    }
  };

  const deleteHandbook = async (handbook: Handbook, isPermanent = false): Promise<void> => {
    if (!handbook.itemId || !handbook.orgId) {
      console.error('Handbook itemId or orgId not found:', handbook.itemId, handbook.orgId);
      return;
    }

    if (!isOrgAdmin(handbook.orgId)) {
      console.error('Permission denied. User:', userState.userProfile?.itemId, 'Handbook:', handbook.itemId);
      return;
    }

    try {
      const handbookRef = doc(firestore, `org/${handbook.orgId}/handbook/${handbook.itemId}`);

      if (isGod && isPermanent) {
        await deleteDoc(handbookRef);
      } else {
        await setDoc(handbookRef, { status: HANDBOOK_STATUSES.DELETED }, { merge: true });
      }
    } catch (error) {
      console.error('Error deleting handbook:', error);
    }
  };

  const updateHandbook = async (handbook: Partial<Handbook>): Promise<void> => {
    if (!handbook.orgId || !handbook.itemId) {
      console.error('Missing required fields:', { orgId: handbook.orgId, itemId: handbook.itemId });
      return;
    }

    if (!isOrgAdmin(handbook.orgId)) {
      console.error('Permission denied: must be admin of organization');
      return;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { orgId, itemId, history, ...handbookUpdates } = handbook;
      const handbookRef = doc(firestore, `org/${orgId}/handbook/${itemId}`);
      await setDoc(handbookRef, { ...handbookUpdates }, { merge: true });
    } catch (error) {
      console.error('Error updating handbook:', error);
    }
  };

  return {
    addHandbook,
    deleteHandbook,
    updateHandbook,
  };
};
